import { token } from "@amzn/stencil-design-tokens/js/web/utils";
import { Row } from "@amzn/stencil-react-components/layout";
import { ScreenReaderOnly } from "@amzn/stencil-react-components/screen-reader-only";
import { Text, TextVariant } from "@amzn/stencil-react-components/text";
import IconCheckSmall from "@amzn/stencil-react-icons/icons/icon-check-small";
import IconCrossSmall from "@amzn/stencil-react-icons/icons/icon-cross-small";
import styled, { CSSObject } from "@emotion/styled";
import React, { FC } from "react";
import { BorderedCard } from "@/common/components/BorderedCard/bordered-card";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { STEP_INDICATOR_SIZE_PX } from "@/pages/OrderTracking/components/OrderProgressSteps/order-progress-steps.constants";
import type { ProgressStep, ProgressStepState } from "@/pages/OrderTracking/order-tracking.types";

const PROGRESS_STRINGS = "orderTracking.progress";

type ConnectorTone = "hidden" | "reached" | "pending";

const DARK_LINE_COLOR = token("color.action.utility.selected-border");

const CONNECTOR_COLORS: Record<ConnectorTone, string> = {
  hidden: "transparent",
  reached: DARK_LINE_COLOR,
  pending: token("color.action.utility.border"),
};

const ringStyles = (borderWidth: string, borderColor: string): CSSObject => ({
  backgroundColor: token("color.fill.neutral.primary"),
  border: `${borderWidth} solid ${borderColor}`,
});

const INDICATOR_STYLES: Record<ProgressStepState, CSSObject> = {
  complete: { backgroundColor: token("color.action.primary.bg-default") },
  current: ringStyles(token("dimensions.border.width.500"), token("color.status.info-border")),
  upcoming: ringStyles(token("dimensions.border.width.200"), DARK_LINE_COLOR),
  failed: { backgroundColor: token("color.action.primary.critical-bg-default") },
  cancelled: { backgroundColor: token("color.status.neutral-fill") },
};

const SCREEN_READER_STATUS: Partial<Record<ProgressStepState, string>> = {
  complete: `${PROGRESS_STRINGS}.completedStatus`,
  upcoming: `${PROGRESS_STRINGS}.upcomingStatus`,
  failed: `${PROGRESS_STRINGS}.failedStatus`,
  cancelled: `${PROGRESS_STRINGS}.cancelledStatus`,
};

const StepList = styled.ol({
  display: "flex",
  alignItems: "flex-start",
  width: "100%",
  margin: 0,
  padding: 0,
  listStyle: "none",
});

const StepItem = styled.li({
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: token("dimensions.spacing.100"),
});

const Connector = styled.span<{ tone: ConnectorTone }>(({ tone }) => ({
  flex: "1 1 0",
  height: token("dimensions.border.width.200"),
  borderRadius: token("dimensions.border.radius.round"),
  backgroundColor: CONNECTOR_COLORS[tone],
}));

const Indicator = styled.span<{ state: ProgressStepState }>(({ state }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  boxSizing: "border-box",
  width: STEP_INDICATOR_SIZE_PX,
  height: STEP_INDICATOR_SIZE_PX,
  borderRadius: token("dimensions.border.radius.round"),
  ...INDICATOR_STYLES[state],
}));

const INVERTED_ICON_COLOR = token("color.icon.primary-inverted-default");

const StepIcon: FC<{ state: ProgressStepState }> = ({ state }) => {
  if (state === "complete") {
    return <IconCheckSmall aria-hidden={true} color={INVERTED_ICON_COLOR} />;
  }
  if (state === "failed" || state === "cancelled") {
    return <IconCrossSmall aria-hidden={true} color={INVERTED_ICON_COLOR} />;
  }
  return null;
};

interface StepProps {
  step: ProgressStep;
  leftConnector: ConnectorTone;
  rightConnector: ConnectorTone;
}

const Step: FC<StepProps> = ({ step, leftConnector, rightConnector }) => {
  const statusKey = SCREEN_READER_STATUS[step.state];

  return (
    <StepItem aria-current={step.state === "current" ? "step" : undefined}>
      <Row gridGap="S200" alignItems="center" width="100%">
        <Connector tone={leftConnector} data-testid="progress-step-connector" aria-hidden={true} />
        <Indicator state={step.state} data-testid="progress-step-indicator" aria-hidden={true}>
          <StepIcon state={step.state} />
        </Indicator>
        <Connector tone={rightConnector} data-testid="progress-step-connector" aria-hidden={true} />
      </Row>
      <Text variant={TextVariant.BodyMDInteractive} color="color.text.primary" textAlign="center">
        {step.label}
      </Text>
      {statusKey && <ScreenReaderOnly>{getString(statusKey)}</ScreenReaderOnly>}
    </StepItem>
  );
};

const MARKER_STATES: ProgressStepState[] = ["current", "failed", "cancelled"];

const isMarker = (step: ProgressStep) => MARKER_STATES.includes(step.state);

/** Connectors are dark up to and including the current, failed or cancelled step, and grey after it. */
const connectorTones = (
  index: number,
  markerIndex: number,
  lastIndex: number
): Pick<StepProps, "leftConnector" | "rightConnector"> => ({
  leftConnector: index === 0 ? "hidden" : index <= markerIndex ? "reached" : "pending",
  rightConnector: index === lastIndex ? "hidden" : index < markerIndex ? "reached" : "pending",
});

export interface OrderProgressStepsProps {
  steps: ProgressStep[];
}

export const OrderProgressSteps: FC<OrderProgressStepsProps> = ({ steps }) => {
  if (steps.length === 0) {
    return null;
  }

  const foundMarker = steps.findIndex(isMarker);
  const markerIndex = foundMarker === -1 ? steps.length : foundMarker;
  const lastIndex = steps.length - 1;

  return (
    <BorderedCard borderRadius="200" padding="S400">
      <StepList aria-label={getString(`${PROGRESS_STRINGS}.label`)}>
        {steps.map((step, index) => (
          <Step
            key={`${index}-${step.label}`}
            step={step}
            {...connectorTones(index, markerIndex, lastIndex)}
          />
        ))}
      </StepList>
    </BorderedCard>
  );
};
